import React, { useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { withFormik, FormikProps } from 'formik';
import {
  Grid,
  Box,
  Checkbox,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Lock } from 'react-feather';
import { useSelector } from 'react-redux';

import { translate } from 'helpers/intl';
import SelectInputField from 'components/SelectInputField';
import InputField from 'components/InputField';
import messages from 'containers/SignupPages/CreatePassword/messages';
import { Captions } from 'components/Typography';
import ActionsButtons from 'containers/SignupPages/ActionsButtons';
import { QuestionSecret } from 'containers/SignupPages/CreatePassword';
import validationSchema from 'containers/SignupPages/CreatePassword/ValidationSchema';
import PasswordInputField from 'components/InputField/Password';
import PasswordProgressBar from 'components/PasswordProgressBar';
import {
  StyledShortBody,
  WrapperBar,
  StyledBodyText,
  SizedLongBody,
  GeneralConditions,
} from 'containers/SignupPages/CreatePassword/styles';
import Modal from 'components/Modal';
import { OrangeButton } from 'components/Button';
import GaClickHOC from 'helpers/GATracking/GaClickHOC';
import { EVENT_CATEGORIES } from 'helpers/GATracking';
import { getLocaleSelector } from 'store/LanguageProvider/selectors';
import { cgu } from 'translations/cgu';
import { truncate } from 'lodash';

const CreatePassword = (
  props: {
    isLoading: boolean;
    getQuestionSecret: Function;
    questionSecret: QuestionSecret[];
    onClickPrevious: Function;
  } & FormikProps<FormValues>,
) => {
  const {
    values,
    touched,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    isLoading,
    onClickPrevious,
    setFieldValue,
  } = props;

  const language = useSelector(getLocaleSelector);
  const [open, setOpen] = useState(false);

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleIsTermsAccepted = () => {
    setFieldValue('isAcceptConditions', true);
    setOpen(false);
  };
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  return (
    <React.Fragment>
      <form onSubmit={handleSubmit}>
        <Box mb={5} paddingX={[4, 8, 12]}>
          <Grid container spacing={3} direction="column" justify="flex-start">
            <Grid item xs={12}>
              <PasswordInputField
                gAEventCategory={EVENT_CATEGORIES.LOGIN_PARAMETERS}
                name="password"
                placeholder={translate({ ...messages.password }, props)}
                value={values.password}
                error={errors.password && touched.password}
                helperText={translate({ ...messages.passwordMsg }, props)}
                startAdornment={<Lock />}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
              <WrapperBar>
                <PasswordProgressBar value={values.password} />
              </WrapperBar>
            </Grid>

            <Grid item xs={12}>
              <PasswordInputField
                gAEventCategory={EVENT_CATEGORIES.LOGIN_PARAMETERS}
                name="confirmPassword"
                placeholder={translate({ ...messages.confirmPassword }, props)}
                value={values.confirmPassword}
                error={errors.confirmPassword && touched.confirmPassword}
                helperText={touched.confirmPassword && errors.confirmPassword}
                startAdornment={<Lock />}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledShortBody>
                {translate({ ...messages.questionTitle }, props)}
              </StyledShortBody>
            </Grid>
            <Grid item xs={12}>
              <SelectInputField
                name="questionSecret"
                SelectDisplayProps={{
                  'data-testid': 'selectQuestionSecret',
                }}
                label={translate({ ...messages.question }, props)}
                onChange={handleChange}
                value={values.questionSecret}
                data={props.questionSecret}
                handleBlur={handleBlur}
                Children={[...props.questionSecret].map((item: any) => (
                  <MenuItem value={item.value} key={item.label}>
                    {matches
                      ? truncate(item.label, { length: 40 })
                      : item.label}
                  </MenuItem>
                ))}
              />
            </Grid>
            <Grid item xs={12}>
              <InputField
                name="response"
                label={translate({ ...messages.response }, props)}
                value={values.response}
                error={errors.response && touched.response}
                helperText={touched.response && errors.response}
                handleChange={handleChange}
                handleBlur={handleBlur}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Box marginBottom={2}></Box>
            <Grid
              container
              direction="row"
              spacing={matches ? 5 : 3}
              alignItems="flex-start"
            >
              <Grid xs={1} item>
                <GaClickHOC
                  category={EVENT_CATEGORIES.LOGIN_PARAMETERS}
                  label="Checkbox Conditions générales"
                >
                  <Checkbox
                    color="primary"
                    name="isAcceptConditions"
                    value={values.isAcceptConditions}
                    onChange={handleChange}
                    checked={values.isAcceptConditions}
                    data-testid="is-terms-accepted"
                  />
                </GaClickHOC>
              </Grid>
              <Grid xs={10} item>
                <Box marginTop={1}>
                  <Captions>
                    {translate({ ...messages.iAccept }, props)}
                    <GeneralConditions
                      data-testid="terms-and-conditions"
                      onClick={() => {
                        handleOpenModal();
                      }}
                    >
                      {' '}
                      {translate({ ...messages.termsAndConditions }, props)}
                    </GeneralConditions>
                    {translate({ ...messages.personalData }, props)}
                  </Captions>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <ActionsButtons
          nextGACategory={EVENT_CATEGORIES.LOGIN_PARAMETERS}
          previousGACategory={EVENT_CATEGORIES.LOGIN_PARAMETERS}
          onClickPrevious={onClickPrevious}
          isLoading={isLoading}
          disableNext={!values.isAcceptConditions}
        />
      </form>
      <Modal
        open={open}
        handleOpen={handleOpenModal}
        handleClose={handleCloseModal}
        header={
          <SizedLongBody weight="500" color="#04336B">
            {translate({ ...messages.modalTitle }, props)}
          </SizedLongBody>
        }
        content={<StyledBodyText>{cgu[language]}</StyledBodyText>}
        footer={
          <Grid spacing={5} container justify="flex-end">
            <Grid item>
              {' '}
              <OrangeButton onClick={handleCloseModal}>
                {translate({ ...messages.close }, props)}
              </OrangeButton>
            </Grid>
            <Grid item>
              <GaClickHOC
                category={EVENT_CATEGORIES.LOGIN_PARAMETERS}
                label="valider"
              >
                <OrangeButton
                  variant="contained"
                  onClick={handleIsTermsAccepted}
                >
                  {translate({ ...messages.accept }, props)}
                </OrangeButton>
              </GaClickHOC>
            </Grid>
          </Grid>
        }
      ></Modal>
    </React.Fragment>
  );
};

export interface FormProps {
  intl: IntlShape;
  isLoading: boolean;
  getQuestionSecret: Function;
  questionSecret: QuestionSecret[];
  onClickPrevious: Function;
  onSubmitStep: Function;
}

interface FormValues {
  questionSecret: string;
  response: string;
  confirmPassword: string;
  password: string;
  isAcceptConditions: boolean;
}

const Form = withFormik<FormProps, FormValues>({
  mapPropsToValues: () => {
    return {
      questionSecret: '',
      response: '',
      confirmPassword: '',
      password: '',
      isAcceptConditions: false,
    };
  },
  validationSchema,
  handleSubmit: (
    { questionSecret, response, confirmPassword, password },
    { props },
  ) => {
    return props.onSubmitStep({
      questionSecret,
      response,
      confirmPassword,
      password,
    });
  },
})(CreatePassword);

export default injectIntl(Form);
